import chroma from "chroma-js";
import format from "@/services/Utils/format";
import { formatBarAxisValue, formatCompact } from "@/features/Charts/utils/formatAxisValue";
import { readChartTokens, type ChartTokens } from "@/features/Charts/config/chartTokens";

// Types
import { ChartType, ChartSettingsType, ChartDataType } from "@/features/Charts";

function getMaxValueLength(data: ChartDataType) {
  const thresholdSmall = 0.0001; // Numbers below this threshold will be in scientific notation
  const thresholdLarge = 1e5; // Numbers above this threshold will be in scientific notation

  let maxLength = 0;

  for (const series of Object.values(data)) {
    series.entries.forEach((entry) => {
      let length;

      if (Math.abs(entry.value) < thresholdSmall || Math.abs(entry.value) >= thresholdLarge) {
        // Convert to scientific notation and get its length
        length = Number(entry.value).toExponential(1).length;
      } else {
        // Format to a maximum of 5 decimal places and get its length
        const formattedValue = Number(entry.value).toFixed(Math.min(3, (entry.value.toString().split(".")[1] || "").length));
        length = formattedValue.length;
      }

      // Update maxLength if this value's length is greater
      if (length > maxLength) {
        maxLength = length;
      }
    });
  }

  return maxLength;
}

function formatValue(value: number) {
  const thresholdSmall = 0.0001; // Small threshold for scientific notation
  const thresholdLarge = 100000; // Large threshold for scientific notation

  if (value === 0) {
    // Return 0 if value is 0
    return "0";
  }

  if (Math.abs(value) < thresholdSmall || Math.abs(value) >= thresholdLarge) {
    // Convert to scientific notation with 1 decimal places
    return value.toExponential(1);
  } else {
    // Otherwise, return the standard number format
    return format.numberWithDecimals(value, 2);
  }
}

// Compact axis labels (e.g. 1.2k, 3.4M) — keeps stacked totals readable instead of falling
// back to scientific notation once values pass 100k. Lives in utils/formatAxisValue so it can be
// unit tested; imported here for the line-chart axis.

function getGrid(chart: ChartType, settings: ChartSettingsType, data: ChartDataType) {
  // Count number of series
  const seriesCount = Object.keys(data).length;
  const legendPadding = 32 * seriesCount;
  const types = {
    HorizontalBarChart: {
      top: settings.legend ? legendPadding : 25,
      bottom: 50,
      left: 25,
      // Leave room on the right for the per-bar label when enabled. "total" also prints the share,
      // so it needs the wider gutter; "value" is just the number.
      right: settings.valueLabel === "total" ? 96 : settings.valueLabel === "value" ? 64 : 50,
      containLabel: true,
    },
    VerticalBarChart: {
      top: settings.legend ? legendPadding : 50,
      bottom: 25,
      left: 50,
      right: 25,
      containLabel: true,
    },
    StackedVerticalBarChart: {
      top: settings.legend ? legendPadding : 50,
      bottom: 25,
      left: 50,
      right: 25,
      containLabel: true,
    },
    LineChart: {
      top: settings.legend ? legendPadding : 20,
      bottom: 40,
      left: 56,
      right: 20,
      containLabel: true,
    },
  };

  return types[chart];
}

function buildHorizontalBarChart(tokens: ChartTokens) {
  return {
    series: {
      type: "bar",
      barMinHeight: 4,
      barMaxWidth: 18,
      itemStyle: {
        borderRadius: [0, 4, 4, 0],
      },
    },
    xAxis: {
      type: "value",
      nameLocation: "middle",
      nameGap: 40,
      // The axis unit caption otherwise inherits the shared theme's 10px. Set here rather than on the
      // theme so VerticalBarChart and LineChart keep their current sizing.
      nameTextStyle: {
        fontSize: 12, // t-caption
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: tokens.splitLineSubtle, // quieter than the shared splitLine token
        },
      },
      axisLine: {
        lineStyle: {
          show: true,
          color: tokens.splitLineSubtle, // the dark zero baseline is the yAxis line below
        },
      },
      axisLabel: {
        textStyle: {
          fontFamily: "Inter, sans-serif",
          fontSize: 12, // t-caption – the app's smallest UI text size
          fontWeight: 500,
          color: tokens.text,
        },
        formatter: function (value: number) {
          return formatBarAxisValue(value);
        },
        hideOverlap: true,
      },
    },
    yAxis: {
      type: "category",
      // Deliberately kept dark: on a value axis that crosses zero ECharts draws this line at x = 0,
      // so it reads as the zero baseline — which matters on charts with a negative module D.
      axisLine: {
        lineStyle: {
          color: tokens.axisLine,
        },
      },
      splitLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        // Default budget for category names. Callers can widen it via `settings.categoryLabelWidth`
        // when they have the room (the deep-dive modal does).
        width: 120,
        padding: [0, 5, 0, 0],
        overflow: "truncate",
        textStyle: {
          fontSize: 12, // t-caption
        },
      },
    },
  };
}

function buildVerticalBarChart(tokens: ChartTokens) {
  return {
    series: {
      type: "bar",
      barMinHeight: 4,
      barMaxWidth: 28,
      itemStyle: {
        borderRadius: [4, 4, 0, 0],
      },
    },
    xAxis: {
      type: "category",
      axisLine: {
        lineStyle: {
          color: tokens.axisLine,
        },
      },
      splitLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        width: 120,
        rotate: 90,
        padding: [0, 5, 0, 0],
        overflow: "truncate",
        textStyle: {
          fontSize: 11,
        },
      },
    },
    yAxis: {
      type: "value",
      nameLocation: "middle",
      splitLine: {
        show: true,
        lineStyle: {
          color: tokens.splitLine,
        },
      },
      axisLine: {
        lineStyle: {
          show: true,
          color: tokens.axisLine,
        },
      },
      axisLabel: {
        textStyle: {
          fontFamily: "Inter, sans-serif",
          fontSize: 10,
          fontWeight: 600,
          color: tokens.text,
        },
        formatter: function (value: number) {
          return formatValue(value); // Use the formatValue function to format y-axis labels
        },
      },
    },
  };
}

function buildLineChart(tokens: ChartTokens) {
  return {
    series: {
      type: "line",
    },
    xAxis: {
      type: "category",
      // Line sits flush against the y-axis — cleaner for a time series than padded ends
      boundaryGap: false,
      axisLine: {
        lineStyle: {
          color: tokens.splitLineSubtle, // faint baseline
        },
      },
      axisTick: {
        show: false,
      },
      splitLine: {
        show: false,
      },
      axisLabel: {
        textStyle: {
          fontFamily: "Inter, sans-serif",
          fontSize: 11,
          color: tokens.text,
        },
      },
    },
    yAxis: {
      type: "value",
      nameLocation: "middle",
      // Drop the heavy dark axis line; keep only subtle horizontal gridlines
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: tokens.splitLineSubtle,
        },
      },
      axisLabel: {
        textStyle: {
          fontFamily: "Inter, sans-serif",
          fontSize: 10,
          fontWeight: 600,
          color: tokens.text,
        },
        formatter: function (value: number) {
          return formatCompact(value);
        },
      },
    },
  };
}

function buildChartConfig(chart: ChartType, tokens: ChartTokens) {
  const builders = {
    HorizontalBarChart: buildHorizontalBarChart,
    VerticalBarChart: buildVerticalBarChart,
    LineChart: buildLineChart,
  };

  return builders[chart](tokens);
}

function getTheme(tokens: ChartTokens) {
  const theme = {
    color: tokens.colorSets.map((colorSet) => chroma.scale(colorSet).mode("lab")(1).hex()),

    // Text style
    textStyle: {
      fontFamily: "Inter, sans-serif",
      fontWeight: 500,
      color: tokens.text,
      fontSize: 10,
    },

    legend: {
      top: -10,
      padding: 10,
      itemWidth: 16,
      itemHeight: 16,
      itemGap: 10,
      textStyle: {
        fontSize: 12,
        fontWeight: 600,
        // ECharts' own legend default is a hardcoded #333, not inherited from textStyle above.
        color: tokens.text,
      },
    },

    animationDurationUpdate: 400,
  };

  return theme;
}

function getThemeColor(index: number): string {
  const tokens = readChartTokens();
  return chroma
    .scale(tokens.colorSets[index % tokens.colorSets.length])
    .mode("lab")(1)
    .hex();
}

function getThemeColorShade(seriesIndex: number, t: number): string {
  const tokens = readChartTokens();
  return chroma
    .scale(tokens.colorSets[seriesIndex % tokens.colorSets.length])
    .mode("lab")(t)
    .hex();
}

function getColor({ index, value, series }: { index: number; value: number; series: object }): string {
  const tokens = readChartTokens();
  // Get highest value in the series
  const max = Math.max(
    ...Object.entries(series)
      .map(([_, x]) => x.entries.map((entry: { value: number }) => entry.value))
      .flat()
  );

  // Return color based on value (gradient within the series' color set)
  return chroma
    .scale(tokens.colorSets[index])
    .mode("lab")(value / max)
    .hex();
}

function getConfig(chart: ChartType, settings: ChartSettingsType, data: ChartDataType) {
  const tokens = readChartTokens();
  const cfg = buildChartConfig(chart, tokens);

  return {
    name: chart,
    theme: getTheme(tokens),
    grid: getGrid(chart, settings, data),
    series: cfg.series,
    xAxis: cfg.xAxis,
    yAxis: cfg.yAxis,
  };
}

export { getConfig, getColor, getThemeColor, getThemeColorShade, getMaxValueLength };
